import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ibsys/kit';
import { XIcon } from 'lucide-react';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type { AttributeFilter } from '@/entities/dashboards';
import { getAttributeFilterKind } from './attributeFilters';
import { AttributeFilterField } from './AttributeFilterField';

type Props = {
  /** Атрибут из системной конфигурации; может быть undefined для «осиротевшего» фильтра */
  attribute: ConversationAttributeItem | undefined;
  /** Текущее значение фильтра */
  filter: AttributeFilter;
  /** Обновление фильтра */
  onChange: (next: AttributeFilter) => void;
  /** Удаление атрибута из активной панели фильтров */
  onRemove: () => void;
};

/**
 * Ячейка одного атрибутного фильтра дашборда качества.
 * Размещается в общей сетке базовых фильтров. Лейбл — имя атрибута,
 * компонент ввода выбирается по `Attribute.dataType` (см. IL «Фильтрация по атрибутам дашборда»).
 */
export const AttributeFilterCell: FC<Props> = ({ attribute, filter, onChange, onRemove }) => {
  const { t } = useTranslation('qualityDashboard');
  const label = attribute?.name || attribute?.pathDescription || filter.path;
  const kind = getAttributeFilterKind(attribute);

  // Шапка ячейки: имя атрибута + кнопка удаления
  const header = (
    <div className="flex items-center justify-between gap-2 min-h-6">
      <span className="text-sm font-medium truncate" title={label}>
        {label}
      </span>
      <Button
        variant="ghost"
        size="icon"
        icon={<XIcon />}
        aria-label={t('filters.removeAttribute')}
        onClick={onRemove}
      />
    </div>
  );

  if (!attribute || kind === null) {
    return <div className="flex flex-col gap-1">{header}</div>;
  }

  return (
    <div className="flex flex-col gap-1">
      {header}
      <AttributeFilterField attribute={attribute} filter={filter} onChange={onChange} />
    </div>
  );
};
